//* 장례식장별 DN(부고브랜드 × 장례식장 → 꽃집) 연결 현황 조회 훅
//* DN이 없으면 그 장례식장 부고에서 화환 주문 목록이 아예 뜨지 않으므로 미연결 장례식장을 찾는 용도
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useRecoilValue } from 'recoil';

import { useSimpleBugoBrandListHook } from '@shared/api/bugoBrand/bugoBrand.hook';
import { simpleBugoBrandListStateSelector } from '@shared/api/bugoBrand/bugoBrand.selector';
import { DistributionNetwork } from '@shared/api/distributionNetwork/distributionNetwork.interface';
import { queryString } from '@shared/hooks/recoil-query';
import { api_ } from '@shared/plugIn/axios';

//* DN 개수는 (브랜드수 × 장례식장수)까지 커질 수 있어 페이지 단위로 끊어 조회
const DN_FETCH_LIMIT = 20000;
//* 무한루프 방지용 최대 페이지 수
const DN_FETCH_MAX_PAGE = 20;

//* 개수가 많아 id 두 개만 받아온다 (_id도 제외)
type DnIdPair = Pick<DistributionNetwork, 'funeralHomeInfo' | 'bugoBrand'>;

export type FuneralHomeDnStatus = 'none' | 'partial' | 'full';

export type FuneralHomeDnInfo = {
  //* 살아있는 부고브랜드 중 연결된 개수
  connectedCount: number;
  brandCount: number;
  status: FuneralHomeDnStatus;
};

const NONE_DN_INFO: FuneralHomeDnInfo = {
  connectedCount: 0,
  brandCount: 0,
  status: 'none',
};

export const useFuneralHomeDnStatusHook = () => {
  useSimpleBugoBrandListHook();
  const simpleBugoBrandState = useRecoilValue(simpleBugoBrandListStateSelector);

  //* funeralHomeInfoId -> 연결된 bugoBrandId Set
  const [connectedBrandMap, setConnectedBrandMap] = useState<Map<
    string,
    Set<string>
  > | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);

  const fetchDnList = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const map = new Map<string, Set<string>>();
      for (let page = 0; page < DN_FETCH_MAX_PAGE; page += 1) {
        const { data } = await api_.get<DnIdPair[]>(
          `admin/distribution-network/?${queryString({
            options: {
              limit: DN_FETCH_LIMIT,
              skip: page * DN_FETCH_LIMIT,
              sort: { _id: 1 },
            },
          })}&select=${encodeURIComponent('funeralHomeInfo bugoBrand -_id')}`,
        );
        data.forEach((dn) => {
          if (!dn.funeralHomeInfo) return;
          const brandSet = map.get(dn.funeralHomeInfo) ?? new Set<string>();
          //* 브랜드가 삭제된 고아 DN은 연결로 치지 않음
          if (dn.bugoBrand) brandSet.add(dn.bugoBrand);
          map.set(dn.funeralHomeInfo, brandSet);
        });
        if (data.length < DN_FETCH_LIMIT) break;
      }
      setConnectedBrandMap(map);
    } catch (err) {
      console.error(err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDnList();
  }, [fetchDnList]);

  const brandIdSet = useMemo(() => {
    if (simpleBugoBrandState.status !== 'success' || !simpleBugoBrandState.data)
      return null;
    return new Set(simpleBugoBrandState.data.map((brand) => brand._id));
  }, [simpleBugoBrandState.status, simpleBugoBrandState.data]);

  //* funeralHomeInfoId -> DN 연결 현황
  const dnInfoMap = useMemo(() => {
    const map = new Map<string, FuneralHomeDnInfo>();
    if (!connectedBrandMap || !brandIdSet) return map;
    const brandCount = brandIdSet.size;
    connectedBrandMap.forEach((brandSet, funeralHomeInfoId) => {
      let connectedCount = 0;
      brandSet.forEach((brandId) => {
        if (brandIdSet.has(brandId)) connectedCount += 1;
      });
      map.set(funeralHomeInfoId, {
        connectedCount,
        brandCount,
        status:
          connectedCount === 0
            ? 'none'
            : connectedCount < brandCount
            ? 'partial'
            : 'full',
      });
    });
    return map;
  }, [connectedBrandMap, brandIdSet]);

  const ready = !!connectedBrandMap && !!brandIdSet;

  //* DN이 하나도 없는 장례식장은 map에 아예 없으므로 기본값(none)으로 처리
  const getDnInfo = useCallback(
    (funeralHomeInfoId: string): FuneralHomeDnInfo => {
      return (
        dnInfoMap.get(funeralHomeInfoId) ?? {
          ...NONE_DN_INFO,
          brandCount: brandIdSet?.size ?? 0,
        }
      );
    },
    [dnInfoMap, brandIdSet],
  );

  return {
    ready,
    loading: loading || simpleBugoBrandState.status === 'loading',
    error,
    brandCount: brandIdSet?.size ?? 0,
    getDnInfo,
    refetch: fetchDnList,
  };
};
